import { useEffect } from 'react';
import type { FeedEntry } from '~/services/layout';
import {
  AUDIO_PLAYER_OPEN_EVENT,
  type AudioPlayerEventDetail,
  resolveAudioPlayerPlayableEntry,
} from '~/utils/audio-player';

type UseAudioPlayerOpenEventParams = {
  setEntry: React.Dispatch<React.SetStateAction<FeedEntry | null>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  requestIdRef?: React.MutableRefObject<number>;
};

/** Subscribes to the global audio-open event and updates initial player state. */
export function useAudioPlayerOpenEvent({
  setEntry,
  setIsPlaying,
  setIsExpanded,
  requestIdRef,
}: UseAudioPlayerOpenEventParams): void {
  useEffect(() => {
    const handleOpen = (event: Event) => {
      const applyOpen = async () => {
        const customEvent = event as CustomEvent<AudioPlayerEventDetail>;
        const nextEntry = customEvent.detail?.entry;

        if (!nextEntry?.content?.src) {
          return;
        }

        const requestId = requestIdRef ? ++requestIdRef.current : 0;
        const playableEntry = await resolveAudioPlayerPlayableEntry(nextEntry);

        if (
          (requestIdRef && requestId !== requestIdRef.current) ||
          !playableEntry?.content?.src
        ) {
          return;
        }

        setEntry(playableEntry);
        setIsPlaying(true);
        setIsExpanded(false);
      };

      void applyOpen();
    };

    window.addEventListener(AUDIO_PLAYER_OPEN_EVENT, handleOpen);

    return () => {
      window.removeEventListener(AUDIO_PLAYER_OPEN_EVENT, handleOpen);
    };
  }, [requestIdRef, setEntry, setIsExpanded, setIsPlaying]);
}
